import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import useGlynt from './useGlynt';
import {
  markValidationByDocumentAtom,
  markValidationsEnabledAtom,
} from '../../atoms/mark-validation-atoms';
import { useEffect } from 'react';
import { buildMarkValidationSummaryByDocumentId } from './useMarkValidation.utils';

// Re-export for backwards compatibility
export { buildMarkValidationSummaryByDocumentId } from './useMarkValidation.utils';

interface UseMarkValidationOptions {
  trainingSetId: string;
  documentIds: string[];
  enabled?: boolean;
}

export function useMarkValidation({
  trainingSetId,
  documentIds,
  enabled = true,
}: UseMarkValidationOptions) {
  const glynt = useGlynt();
  const validationsEnabled = useAtomValue(markValidationsEnabledAtom);
  const [, setValidationByDocument] = useAtom(markValidationByDocumentAtom);
  const queryClient = useQueryClient();

  const shouldFetch = enabled && validationsEnabled && documentIds.length > 0;

  const queries = useQueries({
    queries: documentIds.map((documentId: string) => ({
      queryKey: ['mark-validation', trainingSetId, documentId],

      queryFn: async () => {
        const glyntInstance = await glynt;
        return glyntInstance.trainingSet.validate(trainingSetId, documentId);
      },

      enabled: shouldFetch,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    }))
  });

  const isLoading = queries.some((result) => result.isLoading);
  const isFetching = queries.some((result) => result.isFetching);
  const isError = queries.some((result) => result.isError);
  const isSuccess = queries.every((result) => result.isSuccess);

  // Update the atom when validation data changes
  useEffect(() => {
    if (queries.length > 0 && isSuccess && !isFetching && !isLoading) {
      const summary = buildMarkValidationSummaryByDocumentId(documentIds, queries);
      setValidationByDocument(summary);
    }
  }, [isSuccess, isLoading, isFetching, JSON.stringify(queries.map((q) => q.data))]);

  // Function to invalidate all mark validation queries (for revalidation)
  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: ['mark-validation', trainingSetId]
    });
  };

  return {
    queries,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    invalidateAll,
  };
}
